
import SocialFeedItemsView from '@/components/social/SocialFeedItemsView.vue';
import { GENERIC_ERROR_DESCRIPTION, SOCIAL_PLATFORMS_METADATA } from '@/constants';
import { SocialFeedItem, SocialFeedPlatform } from '@/types/socialFeed';
import { fetchSocialFeedItems, socialFeedPlatformLabel } from '@/utils/socialFeed';
import { DEFAULT_TIMEZONE, SupportedPlatform } from 'ignite360-core';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

export type ImportFeedItem = SocialFeedItem;
export type ImportPlatform = SocialFeedPlatform;

@Component({
  name: 'import-posts-modal',
  components: { SocialFeedItemsView },
})
export default class ImportPostsModal extends Vue {
  @Prop({ required: true, type: Boolean })
  readonly show!: boolean;

  @Prop({ required: true, type: Object })
  readonly botIdsByPlatform!: Partial<Record<ImportPlatform, string>>;

  @Prop({ required: false, type: Object, default: () => ({}) })
  readonly postingDisabledByPlatform!: Partial<Record<ImportPlatform, boolean>>;

  @Prop({ required: false, type: String, default: () => DEFAULT_TIMEZONE })
  readonly timezone!: string;

  activeTab: ImportPlatform = SupportedPlatform.Instagram;
  isLoading = false;
  loadError: string | null = null;
  itemsByPlatform: Partial<Record<ImportPlatform, ImportFeedItem[]>> = {};

  platformLabel(platform: ImportPlatform): string {
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === platform);
    return meta?.label || socialFeedPlatformLabel(platform);
  }

  platformImage(platform: ImportPlatform): string {
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === platform);
    return meta?.image || '';
  }

  get availableTabs(): Array<{
    value: ImportPlatform;
    label: string;
    image: string;
    disabled: boolean;
  }> {
    const tabs: Array<{
      value: ImportPlatform;
      label: string;
      image: string;
      disabled: boolean;
    }> = [];
    const addTab = (platform: ImportPlatform) => {
      if (!this.botIdsByPlatform[platform]) {
        return;
      }
      tabs.push({
        value: platform,
        label: this.platformLabel(platform),
        image: this.platformImage(platform),
        disabled: !!this.postingDisabledByPlatform[platform],
      });
    };
    addTab(SupportedPlatform.Instagram);
    addTab(SupportedPlatform.FacebookMessenger);
    addTab(SupportedPlatform.LinkedIn);
    addTab(SupportedPlatform.GoogleBusiness);
    return tabs;
  }

  get isActiveTabPostingDisabled(): boolean {
    return !!this.postingDisabledByPlatform[this.activeTab];
  }

  get activeItems(): ImportFeedItem[] {
    return this.itemsByPlatform[this.activeTab] || [];
  }

  @Watch('show')
  async onShowChange(visible: boolean) {
    if (!visible) {
      return;
    }
    const firstEnabledTab = this.availableTabs.find((tab) => !tab.disabled)?.value;
    const firstTab = firstEnabledTab || this.availableTabs[0]?.value;
    if (firstTab) {
      this.activeTab = firstTab;
    }
    await this.loadActiveTab();
  }

  @Watch('activeTab')
  async onActiveTabChange() {
    if (!this.show) {
      return;
    }
    await this.loadActiveTab();
  }

  async loadActiveTab() {
    if (this.isActiveTabPostingDisabled) {
      this.isLoading = false;
      this.loadError = null;
      return;
    }
    if (this.itemsByPlatform[this.activeTab]) {
      return;
    }
    const botId = this.botIdsByPlatform[this.activeTab];
    if (!botId) {
      this.itemsByPlatform = { ...this.itemsByPlatform, [this.activeTab]: [] };
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    try {
      const items = await fetchSocialFeedItems(botId, this.activeTab);
      this.itemsByPlatform = {
        ...this.itemsByPlatform,
        [this.activeTab]: items,
      };
    } catch (e) {
      this.loadError = GENERIC_ERROR_DESCRIPTION;
      this.itemsByPlatform = { ...this.itemsByPlatform, [this.activeTab]: [] };
    } finally {
      this.isLoading = false;
    }
  }

  onSelect(item: ImportFeedItem) {
    this.$emit('select', item);
    this.$emit('update:show', false);
  }
}
